import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { DataDisplayTable } from 'components/data-display';
import { BulletedList } from 'components/listing';
import { DateToLocal } from 'components/dates';
import { Collapsable, ShowHide } from 'components/layout';
import { Tabs } from 'components/navigation';
import { NotificationInline } from 'components/notifications';
import { awaitingSignOffStatusBand, completedStatusBand, medicalInformationTopicKey, OutcomeLevelDisplay, profileInformationTopicKey, RaisedByDisplay, tachoInfringementsTopicKey } from 'pages/employee-reviews/shared';
import { TachoInfringementsSnapshot, TopicSnapshot } from './snapshots';
import { ReviewSignatureSet } from './ReviewSignatureSet';
import './CompletedReviewDetail.css';

const defaultArray = [];
const currentSignatureSetKey = "current";
const dateFormat = "DD MMM YYYY";
const dateTimeFormat = "DD MMM YYYY, HH:mm";
const inaccessibleTopicMessage = "You do not have permission to view the details of this topic";

// The renderer for each topic's frozen snapshot, keyed by the topic key the API returns. A
// topic with no renderer shows its heading and comment only, so a topic the backend records
// before the frontend presents it degrades rather than breaking the record.
const topicSnapshotRenderers = {
    [profileInformationTopicKey]: TopicSnapshot,
    [medicalInformationTopicKey]: TopicSnapshot,
    [tachoInfringementsTopicKey]: TachoInfringementsSnapshot
};

// Frontend copy describing how the review was signed off, keyed by the API's method key.
const signOffMethodSentences = {
    PHYSICAL: "Signed off via physical signature.",
    ONSCREEN: "Signed off via on-screen signature.",
    EMAIL: "Signed off via email sign-off.",
    APP: "Signed off via the Vision app."
};

// How each method reads while it is still waiting on the employee, keyed the same way. Only
// these two can ever be outstanding - a physical or on-screen sign-off is given during the
// review itself - so the other methods have no waiting phrase and never need one.
const awaitingSignOffMethodPhrases = {
    EMAIL: "email",
    APP: "the Vision app"
};

const emailSignOffMethodKey = "EMAIL";
const appSignOffMethodKey = "APP";

// Email and app sign-offs wait on the employee to sign after the manager has
// completed the review; the other methods are signed off during the review itself.
const employeeDrivenSignOffMethods = [emailSignOffMethodKey, appSignOffMethodKey];

function buildCommentValue(comment) {
    if (comment == null || comment === "") {
        return "-";
    }

    return comment;
}

// The read-only presentation of a completed review, shared by the manager-facing record and
// the driver's unauthenticated sign-off page. It renders only from the review it is given:
// no fetching, no route params and nothing that reads a signed-in user, so it is safe to
// render anonymously.
//
// showSignOff covers the Sign Off section as a whole: how it was signed, the signatures held
// against it and whether it is still outstanding. It is written for a manager reading a
// finished record. The driver signing through the emailed link is the subject of that section
// rather than its audience, and needs it ordered around the signature they are about to give,
// so that page turns this off and renders its own.
//
// signOffActions is where a caller places anything that acts on the sign-off. This component
// stays presentational and is rendered anonymously, so the controls themselves - and the
// permission checks behind them - belong to the calling page rather than here.
//
// The Additional triggers section lists the triggers recorded for the driver at the completion,
// as the sentences the API froze then. A review completed with an empty list shows the section
// with "None". A review completed without that step, and the emailed sign-off page the API never
// gives them to, receive no additionalTriggers and show no section.
//
// showHeader false leaves the title to a page that shows it above its own tabs.
//
// title replaces the heading the record leads with. The emailed sign-off page is a page about
// the signature being given rather than a record being read, so it names itself as such.
export function CompletedReviewDetail(
    {
        review = null,
        loading = false,
        linkEmployee = true,
        showSignOff = true,
        signOffActions = null,
        title = null,
        showHeader = true
    }
) {

    const employeeSignsSeparately = employeeDrivenSignOffMethods.includes(review?.signOff?.method);

    const awaitingEmployeeSignOff = review != null &&
        employeeSignsSeparately &&
        review.signOff?.employeeSignedOffDate == null;

    // The API sends the sets of signatures an edit replaced oldest first. The current set is the
    // completer's until an edit resets the sign-off.
    // An earlier set holding no signature at all is left out, so repeated edits before a signature
    // add nothing. The current set shows while it awaits the employee, even with no signature yet,
    // so their empty signature box is always in view.
    function hasSignature(signatureSet) {
        return signatureSet.employeeSignedOffDate != null || signatureSet.managerSignature != null;
    }

    const previousSignatureSets = (review?.signOff?.previousSignOffs ?? defaultArray).filter(hasSignature);

    const currentSignatureSet = {
        ...review?.signOff,
        managerSignedBy: review?.signOff?.managerSignedBy ?? review?.completedBy,
        managerSignedDate: review?.signOff?.managerSignedDate ?? review?.completedDate
    };
    const showCurrentSignatureSet = hasSignature(currentSignatureSet) || awaitingEmployeeSignOff;

    // Once an edit has replaced a set, every set sits under its own header, current first and then
    // newest first.
    const collapsibleSignatureSets = [
        ...(showCurrentSignatureSet ? [{ key: currentSignatureSetKey, signatureSet: currentSignatureSet, replaced: false }] : defaultArray),
        ...[...previousSignatureSets].reverse().map((signatureSet) => ({ key: signatureSet.replacedDate, signatureSet, replaced: true }))
    ];

    // Until a header is clicked the first section is open: the current set, or the newest earlier set
    // when the current one has no signature to show.
    const [toggledSignatureSets, setToggledSignatureSets] = useState(null);
    const defaultExpandedSignatureSets = collapsibleSignatureSets.length > 0 ? [collapsibleSignatureSets[0].key] : defaultArray;
    const expandedSignatureSets = toggledSignatureSets ?? defaultExpandedSignatureSets;

    function toggleSignatureSet(key) {
        if (expandedSignatureSets.includes(key)) {
            setToggledSignatureSets(expandedSignatureSets.filter((expandedKey) => expandedKey !== key));
        } else {
            setToggledSignatureSets([...expandedSignatureSets, key]);
        }
    }

    const [selectedTopicIndex, setSelectedTopicIndex] = useState(0);

    // A different review loaded into the same page starts back on its first topic, with its signature
    // sections open as they are by default.
    useEffect(() => {
        setSelectedTopicIndex(0);
        setToggledSignatureSets(null);
    }, [review?.reviewId]);

    const topics = review?.topics ?? [];
    const selectedTopic = topics[Math.min(selectedTopicIndex, topics.length - 1)] ?? null;

    function buildAdditionalTriggersDisplay() {
        const additionalTriggers = review?.additionalTriggers ?? [];

        if (additionalTriggers.length === 0) {
            return (<span>None</span>);
        }

        return (
            <BulletedList
                listItems={additionalTriggers.map((trigger) => trigger.description)}
                loading={loading}
            />
        );
    }

    function renderSignatureSetTitle(signatureSet, replaced) {
        if (!replaced && awaitingEmployeeSignOff) {
            return (<span>Awaiting the employee's signature</span>);
        }

        return (
            <span>Signed <DateToLocal format={dateTimeFormat}>{signatureSet.employeeSignedOffDate ?? signatureSet.managerSignedDate}</DateToLocal></span>
        );
    }

    function renderSignatureSetMetadata(signatureSet, replaced) {
        if (!replaced) {
            return (<span>Current</span>);
        }

        return (
            <span>Replaced <DateToLocal format={dateFormat}>{signatureSet.replacedDate}</DateToLocal></span>
        );
    }

    function buildTitleDisplay() {
        if (title != null) {
            return title;
        }

        return (
            <Fragment>
                {review?.reviewType?.name} - <DateToLocal format={dateFormat}>{review?.completedDate}</DateToLocal>
            </Fragment>
        );
    }

    function buildStatusDisplay() {
        if (awaitingEmployeeSignOff) {
            return <span className={awaitingSignOffStatusBand.className}>{awaitingSignOffStatusBand.label}</span>;
        }

        return <span className={completedStatusBand.className}>{completedStatusBand.label}</span>;
    }

    // What the Sign Off section leads with once the review is signed off. A review still waiting
    // on the employee has no sentence, as their signature box says so instead; both read off the
    // same flag as the Status field above so that the two can never contradict each other.
    function buildSignOffMethodSentence() {
        const signOff = review?.signOff;

        if (awaitingEmployeeSignOff) {
            return null;
        }

        return signOffMethodSentences[signOff?.method] ?? null;
    }

    // What the employee's empty signature box says while the review waits on them.
    function buildAwaitingSignatureMessage() {
        const methodPhrase = awaitingSignOffMethodPhrases[review?.signOff?.method];

        if (!awaitingEmployeeSignOff || methodPhrase == null) {
            return null;
        }

        return `Awaiting the employee's signature via ${methodPhrase}.`;
    }

    const signOffMethodSentence = buildSignOffMethodSentence();
    const awaitingSignatureMessage = buildAwaitingSignatureMessage();

    // The employee name links to their own Reviews tab, opening in a new tab so the
    // completed record stays put. Matches the complete-review wizard's header. Falls
    // back to plain text while loading, or if the record carries no employee id. The
    // link target is an authenticated route, so a caller rendering this to someone who
    // is not signed in turns it off rather than sending them into the login flow.
    function buildEmployeeDisplay() {
        const employeeId = review?.employee?.id;
        const employeeName = review?.employee?.name;

        return (
            <ShowHide
                evaluator={linkEmployee && !loading && employeeId != null}
                show={(
                    <Link to={`/employee/${employeeId}/reviews`} target="_blank" rel="noopener noreferrer">{employeeName}</Link>
                )}
                hide={employeeName}
            />
        );
    }

    function buildMetaFields() {
        return [
            { key: "employee", header: "Employee", value: buildEmployeeDisplay() },
            { key: "completed", header: "Completed", value: <DateToLocal format={dateTimeFormat}>{review?.completedDate}</DateToLocal> },
            { key: "completed-by", header: "Completed By", value: review?.completedBy },
            { key: "raised-by", header: "Raised By", value: <RaisedByDisplay raisedBy={review?.raisedBy} triggers={review?.triggers} /> },
            { key: "status", header: "Status", value: buildStatusDisplay() }
        ];
    }

    function renderTopicContent(topic) {
        let topicContent;

        // A topic the API withholds arrives with neither its comment nor its captured values.
        if (topic.accessible === false) {
            topicContent = (
                <div className="spacer-bottom">
                    <NotificationInline
                        allowClose={false}
                        show
                        text={inaccessibleTopicMessage}
                        intent="warning"
                    />
                </div>
            );
        } else {
            const SnapshotRenderer = topicSnapshotRenderers[topic.topicKey];

            // The comment is handed to the snapshot rather than rendered above it, so each snapshot
            // places it alongside its own captured values.
            const commentField = {
                key: "topic-comment",
                header: "Topic comment",
                value: buildCommentValue(topic.comment),
                fullWidth: true
            };

            if (SnapshotRenderer == null) {
                topicContent = (
                    <div className="spacer-bottom-small">
                        <DataDisplayTable loading={loading} displayInRows={false} data={[commentField]} />
                    </div>
                );
            } else {
                topicContent = (
                    <div className="spacer-bottom-small">
                        <SnapshotRenderer topic={topic} data={topic.data} loading={loading} leadingFields={[commentField]} />
                    </div>
                );
            }
        }

        return topicContent;
    }

    // Each topic is shown on a tab of its own, with only the selected topic rendered. A review
    // covering a single topic has nothing to switch between, so it is titled instead.
    function renderTopics() {
        if (selectedTopic == null) {
            return null;
        }

        if (topics.length === 1) {
            return (
                <Fragment>
                    <h2>{selectedTopic.topicName}</h2>
                    {renderTopicContent(selectedTopic)}
                </Fragment>
            );
        }

        const topicTabs = topics.map((topic) => ({
            title: topic.topicName,
            selected: topic === selectedTopic
        }));

        return (
            <Fragment>
                <div className="spacer-bottom-small">
                    <Tabs tabs={topicTabs} onClick={setSelectedTopicIndex} />
                </div>
                <div key={selectedTopic.topicKey} role="tabpanel" aria-label={selectedTopic.topicName}>
                    {renderTopicContent(selectedTopic)}
                </div>
            </Fragment>
        );
    }

    function renderHeader() {
        return (
            <div className="row">
                <h1 className={classNames({ "bp6-skeleton": loading })}>
                    {buildTitleDisplay()}
                </h1>
            </div>
        );
    }

    return (
        <Fragment>
            <ShowHide
                evaluator={showHeader}
                show={renderHeader()}
            />
            <div className="row">
                <div className={classNames("spacer-bottom-small", { "completed-review-summary-beside-tabs": !showHeader })}>
                    <DataDisplayTable data={buildMetaFields()} displayInRows={false} columnsPerRow={4} loading={loading} />
                </div>
            </div>
            <div className="row">
                <h2>Outcome</h2>
                <DataDisplayTable loading={loading} displayInRows={false} data={[
                    {
                        key: "outcome-level",
                        header: "Outcome level",
                        value: <OutcomeLevelDisplay outcomeLevel={review?.outcomeLevel} />,
                        fullWidth: true
                    },
                    {
                        key: "outcome-summary",
                        header: "Outcome summary",
                        value: review?.outcomeSummary ?? "-",
                        fullWidth: true
                    },
                    {
                        key: "next-review-date",
                        header: "Next review date",
                        value: <DateToLocal format={dateFormat}>{review?.nextReviewDate}</DateToLocal>,
                        fullWidth: true
                    }
                ]} />
                {renderTopics()}
                <ShowHide
                    evaluator={review?.additionalTriggers != null}
                    show={(
                        <Fragment>
                            <h2>Additional triggers</h2>
                            <DataDisplayTable loading={loading} displayInRows={false} data={[
                                {
                                    key: "additional-triggers",
                                    header: "Triggers for this employee",
                                    value: buildAdditionalTriggersDisplay(),
                                    fullWidth: true
                                }
                            ]} />
                        </Fragment>
                    )}
                />
                <ShowHide
                    evaluator={showSignOff}
                    show={(
                        <Fragment>
                            <h2>Sign Off</h2>
                            <ShowHide
                                evaluator={signOffMethodSentence != null}
                                show={(
                                    <p className={classNames("bp6-text-muted", { "spacer-bottom-medium": previousSignatureSets.length > 0 })}>{signOffMethodSentence}</p>
                                )}
                            />
                            <ShowHide
                                evaluator={previousSignatureSets.length > 0}
                                show={collapsibleSignatureSets.map(({ key, signatureSet, replaced }) => (
                                    <div key={`sign-off-${key}`}>
                                        <Collapsable
                                            fullWidth
                                            title={renderSignatureSetTitle(signatureSet, replaced)}
                                            metadata={renderSignatureSetMetadata(signatureSet, replaced)}
                                            expanded={expandedSignatureSets.includes(key)}
                                            onClick={() => toggleSignatureSet(key)}
                                        >
                                            <div className="spacer-top-small completed-review-signature-section">
                                                <ReviewSignatureSet
                                                    signatureSet={signatureSet}
                                                    employeeName={review?.employee?.name}
                                                    replaced={replaced}
                                                    employeeSignsSeparately={employeeSignsSeparately}
                                                    awaitingMessage={replaced ? null : awaitingSignatureMessage}
                                                    spaced={false}
                                                />
                                            </div>
                                        </Collapsable>
                                    </div>
                                ))}
                                hide={(
                                    <ShowHide
                                        evaluator={showCurrentSignatureSet}
                                        show={(
                                            <ReviewSignatureSet
                                                signatureSet={currentSignatureSet}
                                                employeeName={review?.employee?.name}
                                                awaitingMessage={awaitingSignatureMessage}
                                            />
                                        )}
                                    />
                                )}
                            />
                            <ShowHide
                                evaluator={signOffActions != null}
                                show={signOffActions}
                            />
                        </Fragment>
                    )}
                />
            </div>
        </Fragment>
    );
}

CompletedReviewDetail.propTypes = {
    review: PropTypes.object,
    loading: PropTypes.bool,
    linkEmployee: PropTypes.bool,
    showSignOff: PropTypes.bool,
    signOffActions: PropTypes.node,
    title: PropTypes.string,
    showHeader: PropTypes.bool
};
